/** Shared small UI primitives: Button, Chip, Skeleton, Stepper, states. Styles live in styles/ui.css. */
import type { ReactNode } from 'react';
import { Icon } from '@/components/icons/Icon';
import { formatCount } from '@/lib/formatting';
import { LoadingDots } from './Loading';
import '@/styles/ui.css';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  block?: boolean;
  /** Shows motion without resizing the button and blocks duplicate clicks. */
  loading?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  loading = false,
  disabled,
  children,
  className,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const classes = [
    'gs-button',
    `gs-button--${variant}`,
    `gs-button--${size}`,
    block && 'gs-button--block',
    disabled && !loading && 'gs-button--is-disabled',
    className,
  ].filter(Boolean).join(' ');
  return (
    <button {...rest} className={classes} disabled={isDisabled} aria-busy={loading || undefined}>
      <span className="gs-button-content">{children}</span>
      {loading && <span className="gs-button-loading" aria-hidden="true"><LoadingDots /></span>}
    </button>
  );
}

export function Spinner({ size = 15 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        animation: 'gs-spin 700ms linear infinite',
        display: 'inline-block',
      }}
    />
  );
}

export interface ChipProps {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
  /** Renders as a static label rather than a control. */
  readOnly?: boolean;
  tone?: 'neutral' | 'ok' | 'spice' | 'warn';
}

export function Chip({ active = false, onClick, children, readOnly = false, tone = 'neutral' }: ChipProps) {
  if (readOnly) return <span className={`gs-chip gs-chip--${tone}`}>{children}</span>;
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className="gs-chip-button">
      {children}
    </button>
  );
}

export function Skeleton({
  height = 14,
  width = '100%',
  radius = 'var(--gs-radius-xs)',
}: {
  height?: number | string;
  width?: number | string;
  radius?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className="gs-skeleton"
      style={{ display: 'block', height, width, borderRadius: radius }}
    />
  );
}

/** Standard empty state. Always offers a way forward. */
export function EmptyState({
  title,
  body,
  action,
  icon = 'bowl',
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  icon?: Parameters<typeof Icon>[0]['name'];
}) {
  return (
    <div className="gs-empty">
      <div className="gs-empty-art"><Icon name={icon} size={34} weight={1.4} /></div>
      <p className="gs-empty-title">{title}</p>
      {body && <p className="gs-empty-body">{body}</p>}
      {action}
    </div>
  );
}

/** Standard error state with a retry affordance. */
export function ErrorState({
  messageFa,
  onRetry,
  retryable = true,
}: {
  messageFa: string;
  onRetry?: () => void;
  retryable?: boolean;
}) {
  return (
    <div role="alert" className="gs-error">
      <Icon name="warning" size={22} />
      <div>
        <p className="gs-error-message">{messageFa}</p>
        {retryable && onRetry && (
          <div className="gs-error-actions">
            <Button size="sm" onClick={onRetry}>
              تلاش دوباره
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Quantity stepper. Both controls meet the 44px touch target. */
export function Stepper({
  value,
  onDecrement,
  onIncrement,
  min = 1,
  max = 20,
  label,
}: {
  value: number;
  onDecrement: (origin: Element) => void;
  /** Receives the "+" button so callers can animate from it. */
  onIncrement: (origin: Element) => void;
  min?: number;
  max?: number;
  label: string;
}) {
  return (
    <div className="gs-stepper">
      <button type="button" onClick={(event) => onDecrement(event.currentTarget)} aria-label={`کاهش ${label}`}>
        <Icon name="minus" size={17} weight={2} />
      </button>
      <output data-num aria-live="polite" aria-label={`${label}: ${value}`}>
        <span key={value}>{formatCount(value)}</span>
      </output>
      <button type="button" onClick={(event) => onIncrement(event.currentTarget)} aria-label={`افزایش ${label}`} disabled={value >= max}>
        <Icon name="plus" size={17} weight={2} />
      </button>
      <span className="gs-sr-only">{`حداقل ${min}، حداکثر ${max}`}</span>
    </div>
  );
}

/** Persistent, honest demo notice. Never dismissible on payment screens. */
export function DemoNotice({ children }: { children: ReactNode }) {
  return (
    <p className="gs-notice">
      {children}
    </p>
  );
}
